import React, { useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 0.5rem;
  font-size: 1rem;
  font-family: ${({ theme }) => theme.fonts.body};
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 0.5rem;
  font-size: 1rem;
  font-family: ${({ theme }) => theme.fonts.body};
  min-height: 150px;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: ${({ theme }) => theme.fonts.heading};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  box-shadow: ${({ theme }) => theme.shadows.md};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 0.5rem;
  text-align: center;
  font-weight: 500;
  background: ${({ theme, type }) => 
    type === 'success' 
      ? '#d4edda' 
      : '#f8d7da'};
  color: ${({ theme, type }) => 
    type === 'success' 
      ? '#155724' 
      : '#721c24'};
  border: 1px solid ${({ theme, type }) => 
    type === 'success' 
      ? '#c3e6cb' 
      : '#f5c6cb'};
`;

const JoinTodayPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const { request, loading } = useApi();
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status message when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    
    try {
      const response = await request({
        endpoint: `${backendUrl}/api/contact`,
        method: 'POST',
        body: formData
      });
      
      if (response.success) {
        setSubmitStatus({ type: 'success', message: response.message || '상담문의가 성공적으로 전송되었습니다.' });
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      const backendMessage = error.responseData?.error || error.responseData?.message;
      const message = backendMessage
        ? (error.status === 400 ? backendMessage : '이메일 전송에 실패했습니다. 잠시 후 다시 시도해주세요.')
        : (error.message === 'Failed to fetch' || error.message === 'Load failed'
            ? '서버에 연결할 수 없습니다. 백엔드가 실행 중인지 확인해주세요.'
            : '전송 중 오류가 발생했습니다. 다시 시도해주세요.');
      setSubmitStatus({ type: 'error', message });
    }
  };
  
  return (
    <Wrapper>
      <SectionHeading
        eyebrow="Join Today"
        title="상담문의"
        description="에이스코딩학원에 관심을 가져주셔서 감사합니다. 아래 양식을 작성해주시면 빠른 시일 내에 연락드리겠습니다."
        align="center"
      />
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">이름 *</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="이름을 입력해주세요"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">이메일 *</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="이메일을 입력해주세요"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="phone">전화번호 *</Label>
          <Input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="전화번호를 입력해주세요"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">문의내용 *</Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="상담받고 싶은 내용을 입력해주세요"
            required
          />
        </FormGroup>
        
        <SubmitButton type="submit" disabled={loading}>
          {loading ? '전송 중...' : '문의하기'}
        </SubmitButton>
        
        {submitStatus && (
          <Message type={submitStatus.type}>
            {submitStatus.message}
          </Message>
        )}
      </Form>
    </Wrapper>
  );
};

export default JoinTodayPage;

